import { useMemo, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface VoiceBarProps {
  isSpeaking: boolean;
  onPlayPause: () => void;
  isPaused: boolean;
  hasScript: boolean;
  errorMessage?: string | null;
  script?: string;
  charIndex?: number;
}

export function VoiceBar({
  isSpeaking,
  onPlayPause,
  isPaused,
  hasScript,
  errorMessage,
  script,
  charIndex = -1,
}: VoiceBarProps) {
  if (!hasScript) return null;

  const showCaption = !!script && (isSpeaking || isPaused);

  return (
    <div className="bg-white/90 shadow-sm shrink-0 rounded-b-lg border-b border-slate-200">
      {/* Controls row */}
      <div className="flex items-center gap-3 px-4 py-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {isSpeaking && !isPaused ? (
            <Waveform />
          ) : (
            <span className="text-slate-500 text-sm">Tutor</span>
          )}
          <span className={`text-sm truncate ${errorMessage ? "text-red-600" : "text-slate-600"}`}>
            {errorMessage
              ? `Audio unavailable: ${errorMessage}`
              : isSpeaking && !isPaused
                ? "Tutor speaking…"
                : isPaused
                  ? "Tutor paused — tap play to resume"
                  : "Narration plays automatically — tap pause to stop, or Next to continue"}
          </span>
        </div>
        <button
          type="button"
          onClick={onPlayPause}
          className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white transition-transform hover:scale-105"
          style={{ background: "var(--lp-blue)" }}
          aria-label={isSpeaking && !isPaused ? "Pause" : "Play"}
        >
          {isSpeaking && !isPaused ? (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
          ) : (
            <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7L8 5z" />
            </svg>
          )}
        </button>
      </div>

      {/* Word-highlight caption strip */}
      <AnimatePresence>
        {showCaption && (
          <motion.div
            key="caption"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden border-t border-slate-100"
          >
            <SpeakingCaption
              text={script}
              charIndex={charIndex}
              isActive={isSpeaking && !isPaused}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Caption component ────────────────────────────────────────────────────────

interface Token {
  text: string;
  isWord: boolean;
  start: number;
}

function tokenize(text: string): Token[] {
  const tokens: Token[] = [];
  const regex = /(\S+|\s+)/g;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    tokens.push({ text: m[0], isWord: /\S/.test(m[0]), start: m.index });
  }
  return tokens;
}

function findCurrentWordIdx(tokens: Token[], charIndex: number): number {
  // Return the index of the last word whose start is <= charIndex.
  let last = -1;
  for (let i = 0; i < tokens.length; i++) {
    if (!tokens[i].isWord) continue;
    if (tokens[i].start <= charIndex) last = i;
    else break;
  }
  return last;
}

interface SpeakingCaptionProps {
  text: string;
  charIndex: number;
  isActive: boolean;
}

function SpeakingCaption({ text, charIndex, isActive }: SpeakingCaptionProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLSpanElement>(null);

  const tokens = useMemo(() => tokenize(text), [text]);

  const currentIdx = useMemo(
    () => (isActive && charIndex >= 0 ? findCurrentWordIdx(tokens, charIndex) : -1),
    [tokens, charIndex, isActive]
  );

  // Scroll container so the highlighted word stays visible.
  useEffect(() => {
    const container = containerRef.current;
    const word = highlightRef.current;
    if (!container || !word) return;
    const wordTop = word.offsetTop;
    const wordH = word.offsetHeight;
    const containerH = container.clientHeight;
    container.scrollTop = Math.max(0, wordTop - containerH * 0.55 + wordH);
  }, [currentIdx]);

  return (
    <div className="relative">
      <div
        ref={containerRef}
        className="px-4 py-2 max-h-14 overflow-hidden text-sm leading-6 select-none"
        style={{
          WebkitMaskImage:
            "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 20%, rgba(0,0,0,1) 100%)",
          maskImage:
            "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 20%, rgba(0,0,0,1) 100%)",
        }}
      >
        {tokens.map((token, i) => {
          if (!token.isWord) return <span key={i}>{token.text}</span>;
          const isCurrent = i === currentIdx;
          const isPast = currentIdx >= 0 && i < currentIdx;
          return (
            <span
              key={i}
              ref={isCurrent ? highlightRef : undefined}
              className="transition-colors duration-100"
              style={{
                color: isCurrent ? "#1e293b" : isPast ? "#94a3b8" : "#475569",
                background: isCurrent ? "#fde68a" : "transparent",
                borderRadius: isCurrent ? "3px" : undefined,
                padding: isCurrent ? "0 2px" : undefined,
                fontWeight: isCurrent ? 600 : undefined,
              }}
            >
              {token.text}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ─── Waveform animation ───────────────────────────────────────────────────────

function Waveform() {
  return (
    <div className="flex items-center gap-0.5 h-4" aria-hidden>
      {[0.3, 0.6, 1, 0.7, 0.4, 0.8, 0.5, 0.9, 0.5, 0.7].map((h, i) => (
        <motion.span
          key={i}
          className="w-1 rounded-full bg-indigo-500"
          animate={{ height: [4, 16 * h, 4] }}
          transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.05 }}
          style={{ height: 8 }}
        />
      ))}
    </div>
  );
}
